import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Subject, interval } from 'rxjs';
import { takeUntil, switchMap, startWith, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { EventDTO } from '../../../dto/event/event.dto';

@Component({
  selector: 'app-duna-event-info',
  standalone: true,
  imports: [],
  templateUrl: './duna-event-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-event-info.component.scss',
})
export class DunaEventInfoComponent implements OnInit, OnDestroy {
  event: EventDTO | null = null;
  loading = true;
  error: string | null = null;
  private destroy$ = new Subject<void>();
  private lastEventId: number | null = null;

  constructor(
    private dataSourceService: ComponentDataSourceService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Load event data initially and then poll every 5 seconds for updates
    interval(5000)
      .pipe(
        startWith(0),
        switchMap(() => this.loadEventData()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data: EventDTO | null) => {
          if (data) {
            // Only log if the event changed
            if (this.lastEventId !== data.event_id) {
              this.lastEventId = data.event_id;
            }
            this.event = data;
            this.error = null;
          } else {
            this.error = 'Ingen event data modtaget';
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('[DunaEventInfoComponent] Error:', err);
          this.error = 'Fejl ved indlæsning af event';
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadEventData() {
    const dataSource = this.dataSourceService.getDataSource('event');

    if (!dataSource) {
      return of(null);
    }

    try {
      return dataSource.execute().pipe(
        catchError((err) => {
          console.error(
            '[DunaEventInfoComponent] Error loading event:',
            err?.message || err
          );
          return of(null);
        })
      );
    } catch (err) {
      return of(null);
    }
  }

  getEventTypeName(type: string): string {
    switch (type) {
      case 'education':
        return 'Uddannelse';
      case 'course':
        return 'Kursus';
      case 'exercise':
        return 'Øvelse';
      case 'activity':
        return 'Aktivitet';
      default:
        return type;
    }
  }

  getVisibilityName(visibility: string): string {
    switch (visibility) {
      case 'public':
        return 'Offentlig';
      case 'private':
        return 'Privat';
      case 'shared':
        return 'Delt';
      default:
        return visibility;
    }
  }

  getCateringName(catering: string): string {
    switch (catering) {
      case 'self_catering':
        return 'Selvforplejning';
      case 'catering_included':
        return 'Forplejning inkluderet';
      case 'catering_not_included':
        return 'Forplejning ikke inkluderet';
      default:
        return catering;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('da-DK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getLocationName(): string {
    if (this.event?.predefined_location) {
      return this.event.predefined_location.locations_name || '';
    }
    if (this.event?.manual_location) {
      return this.event.manual_location;
    }
    return 'Ikke angivet';
  }

  getRoomName(): string {
    if (this.event?.predefined_room) {
      return this.event.predefined_room.room_name || '';
    }
    return '';
  }
}
